// stytchService.ts
// A single export that either provides a real Stytch-backed service (loaded lazily)
// or a no-op “partner” stub, depending on config.enableStytch.
// Nothing from @stytch is imported at runtime unless the flag is enabled (type-only imports are erased).

import config from "./config";

import type {
  User as StytchUser,
  Session as StytchSession,
  ResponseCommon,
} from "@stytch/vanilla-js";
import type {
  StytchHeadlessClient as StytchHeadlessClientType,
  OAuthStartResponse,
  OTPsLoginOrCreateResponse,
  OTPsAuthenticateResponse,
  OAuthAuthenticateResponse,
} from "@stytch/vanilla-js/headless";

export type StytchService = {
  // One-shot reads
  getUserOnce(fresh?: boolean): Promise<StytchUser | null>;
  getUserSync(): StytchUser | null;
  getSessionOnce(): StytchSession | null;

  // Auth flows (precise types)
  startGoogleOAuth(redirectUrl: string): Promise<OAuthStartResponse>;
  startLinkedInOAuth(redirectUrl: string): Promise<OAuthStartResponse>;
  startGithubOAuth(redirectUrl: string): Promise<OAuthStartResponse>;
  startEmailLogin(email: string): Promise<OTPsLoginOrCreateResponse>;
  authenticateCode(
    verificationCode: string,
    stytchMethodId: string
  ): Promise<OTPsAuthenticateResponse>;
  authenticateOAuth(token: string): Promise<OAuthAuthenticateResponse>;
  logout(): Promise<ResponseCommon>;
  hasStytchSessionSync(): boolean;

  // Debugging
  toString(): string;
};

// ---------------- Stytch-backed implementation (lazy-loaded) ----------------

let clientPromise: Promise<StytchHeadlessClientType> | null = null;
let clientRef: StytchHeadlessClientType | null = null;

function ensureClient(): Promise<StytchHeadlessClientType> {
  if (!clientPromise) {
    clientPromise = import("@stytch/vanilla-js/headless").then(({ StytchHeadlessClient }) => {
      const c = new StytchHeadlessClient(import.meta.env.VITE_STYTCH_PUBLIC_TOKEN!);
      clientRef = c;
      return c;
    });
  }
  return clientPromise;
}

function createStytchService(): StytchService {
  async function getUserOnce(fresh = false): Promise<StytchUser | null> {
    if (!fresh && clientRef) {
      const u = clientRef.user.getSync();
      if (u) return u;
    }
    const client = await ensureClient();
    try {
      return (await client.user.get()) ?? null;
    } catch {
      return null;
    }
  }

  return {
    getUserOnce,
    getUserSync: () => clientRef?.user.getSync() ?? null,
    getSessionOnce: () => clientRef?.session.getSync() ?? null,

    startGoogleOAuth: async (redirectUrl: string): Promise<OAuthStartResponse> => {
      const client = await ensureClient();
      return client.oauth.google.start({
        login_redirect_url: redirectUrl,
        signup_redirect_url: redirectUrl,
      });
    },

    startLinkedInOAuth: async (redirectUrl: string): Promise<OAuthStartResponse> => {
      const client = await ensureClient();
      return client.oauth.linkedin.start({
        login_redirect_url: redirectUrl,
        signup_redirect_url: redirectUrl,
      });
    },

    startGithubOAuth: async (redirectUrl: string): Promise<OAuthStartResponse> => {
      const client = await ensureClient();
      return client.oauth.github.start({
        login_redirect_url: redirectUrl,
        signup_redirect_url: redirectUrl,
      });
    },

    startEmailLogin: async (email: string): Promise<OTPsLoginOrCreateResponse> => {
      const client = await ensureClient();
      return client.otps.email.loginOrCreate(email, {
        expiration_minutes: 10,
        login_template_id: import.meta.env.VITE_LOGIN_TEMPLATE_ID,
        signup_template_id: import.meta.env.VITE_SIGNUP_TEMPLATE_ID,
      });
    },

    authenticateCode: async (
      verificationCode: string,
      stytchMethodId: string
    ): Promise<OTPsAuthenticateResponse> => {
      const client = await ensureClient();
      return client.otps.authenticate(verificationCode, stytchMethodId, {
        session_duration_minutes: 1070,
      });
    },

    authenticateOAuth: async (token: string): Promise<OAuthAuthenticateResponse> => {
      const client = await ensureClient();
      return client.oauth.authenticate(token, { session_duration_minutes: 10080 });
    },

    logout: async (): Promise<ResponseCommon> => {
      const client = await ensureClient();
      return client.session.revoke();
    },

    hasStytchSessionSync: () => !!clientRef?.session.getSync(),

    toString: () => "[Auth Service - Stytch enabled]",
  };
}

// ---------------- Partner (no-Stytch) stub ----------------

function createNullStytchService(): StytchService {
  return {
    getUserOnce: async () => null,
    getUserSync: () => null,
    getSessionOnce: () => null,

    // Cast undefined to the exact response types so the interface stays tight.
    startGoogleOAuth: async () => undefined as unknown as OAuthStartResponse,
    startLinkedInOAuth: async () => undefined as unknown as OAuthStartResponse,
    startGithubOAuth: async () => undefined as unknown as OAuthStartResponse,
    startEmailLogin: async () => undefined as unknown as OTPsLoginOrCreateResponse,
    authenticateCode: async () => undefined as unknown as OTPsAuthenticateResponse,
    authenticateOAuth: async () => undefined as unknown as OAuthAuthenticateResponse,
    logout: async () => undefined as unknown as ResponseCommon,

    hasStytchSessionSync: () => false,

    toString: () => "[Auth Service - Stytch disabled]",
  };
}

// ---------------- Export a single service ----------------

export const stytchService: StytchService = config.enableStytch
  ? createStytchService()
  : createNullStytchService();

console.log("[stytchService] initialized:", stytchService.toString());

// Optional: warm the client when enabled so first call is snappy
if (config.enableStytch) {
  void ensureClient();
}